import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, MessageSquare } from 'lucide-react';

const WHATSAPP_NUMBER = '918900992020';

export function EnquiryForm() {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    checkIn: '',
    checkOut: '',
    guests: '2',
    message: ''
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) return 'Full Name is required.';
    
    // Indian phone number validation: starts with 6-9, followed by 9 digits
    const phoneRegex = /^[6-9]\d{9}$/;
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (!cleanPhone) return 'Phone Number is required.';
    if (cleanPhone.length !== 10 || !phoneRegex.test(cleanPhone)) return 'Please enter a valid 10-digit Indian phone number.';

    if (!formData.checkIn) return 'Check-in Date is required.';
    if (!formData.checkOut) return 'Check-out Date is required.';
    
    const checkInDate = new Date(formData.checkIn);
    const checkOutDate = new Date(formData.checkOut);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (checkInDate < today) return 'Check-in date cannot be in the past.';
    if (checkOutDate <= checkInDate) return 'Check-out date must be after check-in date.';

    if (!formData.guests) return 'Number of Guests is required.';

    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    const error = validateForm();
    if (error) {
      setErrorMessage(error);
      setStatus('error');
      // Scroll to error if mobile
      window.scrollTo({ top: document.getElementById('enquiry-form-error')?.offsetTop ? (document.getElementById('enquiry-form-error')!.offsetTop - 100) : window.scrollY, behavior: 'smooth' });
      return;
    }

    setStatus('submitting');
    
    // Construct WhatsApp Message
    const message = `Hello Walnut Farm,

I would like to make an enquiry.

*Customer Details*
Name: ${formData.fullName}
Phone: ${formData.phone}
Email: ${formData.email || 'Not provided'}

*Stay Details*
Check-in: ${formData.checkIn}
Check-out: ${formData.checkOut}
Guests: ${formData.guests}

*Special Request*
${formData.message || 'No special requests'}

Please share the availability and booking details.

Thank you.`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    // Small delay to show processing state and disable button
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
      setStatus('idle');
    }, 800);
  };

  const labelClasses = "block text-xs md:text-sm font-clean font-bold uppercase tracking-widest !text-[#176B5B] mb-2 text-left px-1";
  const inputClasses = "w-full bg-background/50 border border-[#176B5B]/20 rounded-xl px-4 py-3 text-[#176B5B] placeholder-[#176B5B]/40 focus:outline-none focus:border-[#B8863B] focus:ring-1 focus:ring-[#B8863B]/20 transition-all duration-300 font-clean text-base";

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Full Name */}
          <div className="space-y-2">
            <label htmlFor="fullName" className={labelClasses}>Full Name *</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Enter your name"
              className={inputClasses}
            />
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <label htmlFor="phone" className={labelClasses}>Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="10-digit mobile number"
              className={inputClasses}
            />
          </div>

          {/* Email Address */}
          <div className="space-y-2">
            <label htmlFor="email" className={labelClasses}>Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@domain.com"
              className={inputClasses}
            />
          </div>

          {/* Number of Guests */}
          <div className="space-y-2">
            <label htmlFor="guests" className={labelClasses}>Number of Guests *</label>
            <select
              id="guests"
              name="guests"
              required
              value={formData.guests}
              onChange={handleChange}
              className={inputClasses}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(n => (
                <option key={n} value={n}>{n} Guests</option>
              ))}
              <option value="15+">15+ Guests</option>
            </select>
          </div>

          {/* Check-in Date */}
          <div className="space-y-2">
            <label htmlFor="checkIn" className={labelClasses}>Check-in Date *</label>
            <input
              type="date"
              id="checkIn"
              name="checkIn"
              required
              value={formData.checkIn}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>

          {/* Check-out Date */}
          <div className="space-y-2">
            <label htmlFor="checkOut" className={labelClasses}>Check-out Date *</label>
            <input
              type="date"
              id="checkOut"
              name="checkOut"
              required
              value={formData.checkOut}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <label htmlFor="message" className={labelClasses}>Message / Special Request</label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your requirements or special requests..."
            className={`${inputClasses} resize-none`}
          />
        </div>

        <div id="enquiry-form-error">
          <AnimatePresence>
            {status === 'error' && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center space-x-2 text-red-600 bg-red-50 p-4 rounded-xl border border-red-100 mb-4"
              >
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm font-medium">{errorMessage}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full md:w-auto bg-[#176B5B] text-white px-10 py-5 rounded-full hover:bg-[#087F8C] hover:shadow-2xl hover:shadow-[#176B5B]/20 transition-all duration-500 disabled:opacity-70 disabled:cursor-not-allowed font-clean font-bold uppercase tracking-widest text-sm flex items-center justify-center space-x-3 mx-auto md:mx-0"
          >
            <MessageSquare className="w-4 h-4" />
            <span>{status === 'submitting' ? 'Processing...' : 'Submit enquiry on WhatsApp'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}